import { Buttons } from '../../../buttons';
import { Component, computed, input, output, ChangeDetectionStrategy } from '@angular/core';
import { Conversation, ConversationMessage } from './models/conversation';
import { CommonModule, DatePipe } from '@angular/common';
import { Textbox } from '../../../textbox';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-conversation',
  standalone: true,
  imports: [Textbox, Buttons, CommonModule, FormsModule],
  templateUrl: './conversation.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './conversation.component.scss'
})
export class ConversationComponent {
  conversation = input.required<Conversation>();
  readonly = input<boolean>(false);
  noCommentsText = input<string>('No messages yet.\n Start the conversation by sending a message.');
  postNewMessageClicked = output<ConversationMessage>();

  sortedMessages = computed(() => this.conversation()?.Comments.sort((a, b) => a.Timestamp.getTime() - b.Timestamp.getTime()));
  messageText: string = '';

  getMessageClass(message: ConversationMessage, index: number): string {
    let isStart = false;
    let isEnd = false;
    let isFirstOfDay = false;
    let isLastOfDay = false;

    const isMyMessage = message.UserId.toLocaleLowerCase() === this.conversation()?.UserId.toLocaleLowerCase();
    const isSystemMessage = message.UserId.toLocaleLowerCase() === 'system';

    if (index === 0) {
      isStart = true;
    }
    if (index === this.sortedMessages().length - 1) {
      isEnd = true;
    }
    if (index > 0) {
      const previousMessage = this.sortedMessages()[index - 1];
      if (previousMessage.UserId !== message.UserId) {
        isStart = true;
      }
    }
    if (index < this.sortedMessages().length - 1) {
      const nextMessage = this.sortedMessages()[index + 1];
      if (nextMessage.UserId !== message.UserId) {
        isEnd = true;
      }
      if (this.shouldShowDateDivider(index + 1)) {
        isLastOfDay = true;
      }
    }
    if (this.shouldShowDateDivider(index)) {
      isFirstOfDay = true;
    }

    if (isSystemMessage) {
      return 'system';
    }
    else if (isStart && isEnd) {
      return isMyMessage ? 'me start' : 'them start';
    }
    else if (isStart || isFirstOfDay) {
      return isMyMessage ? 'me start' : 'them start';
    }
    else if (isEnd || isLastOfDay) {
      return isMyMessage ? 'me end' : 'them end';
    }
    else {
      return isMyMessage ? 'me' : 'them';
    }
  }

  shouldShowDateDivider(index: number): boolean {
    if (index === 0) {
      return true;
    }
    const currentMessage = this.sortedMessages()[index];
    const previousMessage = this.sortedMessages()[index - 1];
    return currentMessage.Timestamp.toDateString() !== previousMessage.Timestamp.toDateString();
  }

  formatDate(date: Date): string {
    const pipe = new DatePipe('en-US');
    return pipe.transform(date, 'short');
  }

  postNewMessage() {
    const message = new ConversationMessage({UserId: this.conversation()?.UserId, Message: this.messageText, Timestamp: new Date()});
    this.messageText = null;
    this.postNewMessageClicked.emit(message);
  }
}
